import React, { forwardRef } from "react";
import styled from "styled-components";
import css from "@styled-system/css";
import { Box } from "@bca/ui-library";

const StyledInput = styled.input<
  React.InputHTMLAttributes<HTMLInputElement> & {
    error: boolean;
    warning?: boolean;
    ref?: any;
  }
>`
  box-sizing: border-box;
  height: 48px;
  width: 100%;
  padding: 0 14px;
  outline: none;
  ${css({
    borderRadius: 2,
    fontSize: "body",
    fontFamily: "normal",
    border: "normalBorder",
    boxShadow: "default",
    "&:focus": {
      border: "primaryBorder",
      boxShadow: "accent",
    },
  })};
  ${(props) =>
    props.error
      ? css({
          border: "errorBorder",
          boxShadow: "error",
          "&:focus": {
            boxShadow: "error",
            border: "errorBorder",
          },
        })
      : null}
  ${(props) =>
    props.warning
      ? css({
          border: "warningBorder",
          boxShadow: "warning",
          "&:focus": {
            boxShadow: "warning",
            border: "warningBorder",
          },
        })
      : null}
`;

interface OmitWidth
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "width"> {}

export interface InputProps extends OmitWidth {
  /**
      Allows the user to specify the horizontal padding of the button. Accepts values from the space property in the theme(numbers 0-9 that, multiplied by 4, represent the number of pixels), or strings corresponding to the number of pixels.
      (eg.paddingX: 1 -> padding-left: 4px, padding-right: 4px;
          paddingX: 2 -> padding-left: 8px, padding-right: 8px;
          paddingX: "1px" -> padding-left: 1px, padding-right: 1px;
          paddingX: "2px" -> padding-left: 2px, padding-right: 2px;)
      */
  paddingX?: number | string | (string | number)[];
  children?: any;
  width?: number | string | (string | number)[];
  error: boolean;
  warning?: boolean;
  value?: string | number;
  onChange: (e: any) => void;
}

const CustomInput = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      width,
      paddingX,
      required,
      error,
      value,
      onChange,
      children,
      ...props
    }: InputProps,
    ref
  ) => {
    return (
      <Box
        display="flex"
        flexDirection="column"
        paddingX={paddingX}
        width={width}
        mb={3}
      >
        <StyledInput
          maxLength={30}
          error={error}
          required={required}
          ref={ref}
          value={value}
          onChange={onChange}
          aria-label="input field"
          {...props}
        ></StyledInput>
        {children}
      </Box>
    );
  }
);

export default CustomInput;
